import React, { useRef, useState, useEffect, useCallback } from "react";
import "./AnalogStick.css";

const AnalogStick = ({ onMove }) => {
  const containerRef = useRef(null);
  const knobRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const updateKnob = useCallback((clientX, clientY) => {
    if (!containerRef.current || !knobRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = clientX - centerX;
    const deltaY = clientY - centerY;
    const maxRadius = rect.width / 2 - 10; // leave 10px margin
    const distance = Math.hypot(deltaX, deltaY);
    const angle = Math.atan2(deltaY, deltaX);
    const clampedDistance = distance > maxRadius ? maxRadius : distance;
    const finalX = clampedDistance * Math.cos(angle);
    const finalY = clampedDistance * Math.sin(angle);

    knobRef.current.style.transform = `translate(${finalX}px, ${finalY}px)`;

    // Normalize the values to range from -1 to 1, then reduce sensitivity
    const normalizedX = (finalX / maxRadius) * 0.5; // Reduce speed by 50%
    const normalizedY = (finalY / maxRadius) * 0.5; // Reduce speed by 50%

    if (onMove) onMove({ x: normalizedX, y: normalizedY });
  }, [onMove]);

  const endDrag = useCallback(() => {
    setDragging(false);
    if (knobRef.current) {
      knobRef.current.style.transform = "translate(0px, 0px)";
    }
    if (onMove) onMove({ x: 0, y: 0 });
  }, [onMove]);

  const startDrag = useCallback((clientX, clientY) => {
    setDragging(true);
    updateKnob(clientX, clientY);
  }, [updateKnob]);

  // Attach global mouse events when dragging starts
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (dragging) {
        updateKnob(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = (e) => {
      if (dragging) {
        endDrag();
      }
    };

    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    // Cleanup global event listeners
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, updateKnob, endDrag]);

  // Touch events
  const onTouchStart = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY);
  }, [startDrag]);

  const onTouchMove = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    updateKnob(touch.clientX, touch.clientY);
  }, [updateKnob]);

  const onTouchEnd = useCallback((e) => {
    e.preventDefault();
    endDrag();
  }, [endDrag]);

  // Mouse event: attach global listeners on mouse down
  const onMouseDown = useCallback((e) => {
    e.preventDefault(); // Prevent default behavior like text selection
    startDrag(e.clientX, e.clientY);
  }, [startDrag]);

  return (
    <div
      className="analog-stick-container"
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
    >
      <div className="analog-stick-base">
        <div className="analog-stick-knob" ref={knobRef}></div>
      </div>
    </div>
  );
};

export default AnalogStick;
